import { APP_INITIALIZER, Injector, LOCALE_ID, NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ButtonModule } from 'primeng/button';
import { ConfigService } from './_services/config.service';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { HttpRequestInterceptor } from './_services/http-request-interceptor';
import { SharingModule } from './sharing/sharing.module';
import { FunzioniRuoliModule } from './_directives/funzioni-ruoli.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DatePipe, HashLocationStrategy, LocationStrategy, registerLocaleData } from '@angular/common';
import { NgxSpinnerModule } from 'ngx-spinner';
import { NumberFormatPipeModule } from './sharing/_pipes/numberFormatPipeModule';
import { TranslateLoader, TranslateService, TranslatePipe, TranslateDirective, provideTranslateService } from '@ngx-translate/core';
import { TabellaService } from './_services/tabella.service';
import { CustomTranslateLoader } from './components/customTranslate.loader';
import { CascadeSelectModule } from 'primeng/cascadeselect';
import { MenuModule } from 'primeng/menu';
import { providePrimeNG } from 'primeng/config';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SkeletonModule } from 'primeng/skeleton';
import Aura from '@primeuix/themes/aura';
import { environment } from '../environments/environment';
import { ModalModule } from 'ngx-bootstrap/modal';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TabsModule } from 'ngx-bootstrap/tabs';
import localeIt from '@angular/common/locales/it';
import { ColorPickerModule } from 'primeng/colorpicker';
import { SliderModule } from 'primeng/slider';
import { PopoverModule } from 'primeng/popover';
registerLocaleData(localeIt);

// Import containers
import { FullLayoutComponent, SimpleLayoutComponent } from './containers';
import { MenuHubTabsComponent } from './components/menu-hub-tabs/menu-hub-tabs.component';

const APP_CONTAINERS = [FullLayoutComponent, SimpleLayoutComponent];

// Import components
import { AppMenuEditorComponent } from './components/app-menu-editor/app-menu-editor.component';
import { AppMenuRibbonComponent } from './components/app-menu-ribbon/app-menu-ribbon.component';
import { AppBreadcrumbsComponent, AppFooterComponent, AppHeaderComponent, AppSidebarComponent, AppSidebarFooterComponent, AppSidebarFormComponent, AppSidebarHeaderComponent, AppSidebarMinimizerComponent, APP_SIDEBAR_NAV, APP_TOPNAV, CustomToastComponent } from './components';

const APP_COMPONENTS = [AppBreadcrumbsComponent, AppFooterComponent, AppHeaderComponent, AppSidebarComponent, AppSidebarFooterComponent, AppSidebarFormComponent, AppSidebarHeaderComponent, AppSidebarMinimizerComponent, APP_SIDEBAR_NAV, APP_TOPNAV, CustomToastComponent];

// Import directives
import { AsideToggleDirective, NAV_DROPDOWN_DIRECTIVES, ReplaceDirective, SIDEBAR_TOGGLE_DIRECTIVES } from './directives';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { P503Component } from './views/pages/503.component';
import { P404Component } from './views/pages/404.component';
import { TooltipModule } from 'primeng/tooltip';
import { AvatarModule } from 'primeng/avatar';
import { TagModule } from 'primeng/tag';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProfiloComponent } from './portale/Profilo/profilo.component';
import { ServiceCardsComponent } from './components/service-cards/service-cards.component';
import { SelectModule } from 'primeng/select';
import { ListaAppuntamentiComponent } from './companies/ListaAppuntamenti/lista-appuntamenti.component';
import { FullCalendarModule } from '@fullcalendar/angular';
import { MultiSelectModule } from 'primeng/multiselect';
import { EventoAppuntamentoComponent } from './companies/ListaAppuntamenti/evento/evento-appuntamento.component';
import { ChatPanelComponent } from './messaging/panel/chat-panel.component';
import { NotificationsPanelComponent } from './notifications/panel/notifications-panel.component';
import { ExportJobsPanelComponent } from './_directives/export-jobs/export-jobs-panel.component';
import { CookieConsentComponent } from './components/cookie-consent/cookie-consent.component';

const APP_DIRECTIVES = [AsideToggleDirective, NAV_DROPDOWN_DIRECTIVES, ReplaceDirective, SIDEBAR_TOGGLE_DIRECTIVES];

function initConfig(config: ConfigService) {
	return () => config.load();
}

export const httpInterceptorProviders = [{ provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true }];

@NgModule({
	imports: [
		BrowserModule,
		MenuHubTabsComponent,
		AppRoutingModule,
		ButtonModule,
		CommonModule,
		ReactiveFormsModule,
		FormsModule,
		FunzioniRuoliModule,
		SharingModule,
		BrowserAnimationsModule,
		NgxSpinnerModule,
		HttpClientModule,
		NumberFormatPipeModule,
		ToastModule,
		CascadeSelectModule,
		PopoverModule,
		MenuModule,
		InputIconModule,
		SkeletonModule,
		IconFieldModule,
		DatePickerModule,
		CheckboxModule,
		ColorPickerModule,
		DividerModule,
		MultiSelectModule,
		FullCalendarModule,
		SliderModule,
		PopoverModule,
		TooltipModule,
		AvatarModule,
		TagModule,
		ServiceCardsComponent,
		RadioButtonModule,
		ProfiloComponent,
		SelectModule,
		ChatPanelComponent,
		NotificationsPanelComponent,
		ExportJobsPanelComponent,
		CookieConsentComponent,
		TranslatePipe,
		TranslateDirective,
		BsDropdownModule,
		TabsModule,
		ModalModule,
		AppMenuEditorComponent,
		AppMenuRibbonComponent,
	],
	declarations: [AppComponent, P404Component, P503Component, ...APP_CONTAINERS, ...APP_COMPONENTS, ...APP_DIRECTIVES, ListaAppuntamentiComponent, EventoAppuntamentoComponent],

	providers: [
		provideTranslateService({
			loader: {
				provide: TranslateLoader,
				useClass: CustomTranslateLoader,
				deps: [HttpClient, Injector],
			},
		}),
		provideZoneChangeDetection(),
		provideAnimationsAsync(),
		providePrimeNG({
			license: environment.primeui_license,
			theme: {
				preset: Aura,
				options: {
					prefix: 'p',
					darkModeSelector: '.app-dark',
					cssLayer: false,
				},
			},
			overlayOptions: {
				appendTo: 'body',
			},
		}),
		{
			provide: LocationStrategy,
			useClass: HashLocationStrategy,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: (configService: ConfigService) =>
				function () {
					return initConfig(configService);
				},
			deps: [ConfigService],
			multi: true,
		},
		{
			provide: LOCALE_ID,
			useValue: 'it-IT',
		},

		HttpClient,
		TranslateService,
		ConfigService,
		TabellaService,
		DatePipe,
		MessageService,
		httpInterceptorProviders,
	],
	bootstrap: [AppComponent],
})
export class AppModule {}
